import { Parser } from '~/api';
import { Node, NodeAddon, Tag } from '~/api/models';
import type { LivelinePoint } from '~/components/charts/liveline/types';
import { useModelsStore } from '~/stores';
import RequestService from './request';

interface GetNodeDaemonInfoRequest {
    id: number;
}

interface GetMassNodeDaemonInfoRequest {
    nodes: number[];
}

interface UpdateNodeRequest {
    name: string;
    description?: string;
    public: boolean;
    display_fqdn?: string;
    maintenance_node: boolean;
    location_id: number;

    cpu: number;
    cpu_overallocate?: number;
    memory: number;
    memory_overallocate?: number;
    disk: number;
    disk_overallocate?: number;

    upload_size: number;
    daemonListen: number;
    daemonSFTP: number;
    daemonFastdl: number;

    tags?: string[];
    deployment_tags?: string[];
}

export interface NodeDaemonUtilization {
    cpu_percent: number;
    memory_used_bytes: number;
    memory_total_bytes: number;
}

export interface NodeDaemonInfo {
    alive: boolean;
    version: string;
    system: {
        type: string;
        arch: string;
        kernel: string;
        cpus: number;
    };
    // Whole-machine usage; null for daemons that predate utilization reporting.
    utilization: NodeDaemonUtilization | null;
    // Per-metric 24h peaks (p90 of the recorded samples, floored at the latest
    // one); null while the node's sample history is too thin.
    peaks_24h: { cpu_percent: number; memory_used_bytes: number } | null;
}

export interface NodeUsageHistory {
    // Machine CPU percent (0-100) over the trailing 24h, oldest first.
    cpu: LivelinePoint[];
    // Machine memory bytes used over the trailing 24h, oldest first.
    memory: LivelinePoint[];
    memory_total_bytes: number | null;
    // Fraction (0..1) of the expected 5-minute samples present in the window.
    coverage: number;
}

class NodeService {
    getAll(req: PaginatableRequest): Promise<Node[]> {
        return RequestService.get('/nodes', {
            ...req,
            sort: req.sort ?? 'name',
            include: ['location', 'serversCount', 'tags', 'deploymentTags'],
        }).then(Parser.parse);
    }

    get(): Promise<Node> {
        return RequestService.get('/nodes/:node', {
            include: ['location', 'tags', 'deploymentTags'],
        }).then(Parser.parse);
    }

    syncTags(data: { tags: string[] }): Promise<Tag[]> {
        return RequestService.put('/nodes/:node/tags', data)
            .then(Parser.parse)
            .then((tags) => {
                useModelsStore().refreshModel('node');

                return tags;
            });
    }

    syncDeploymentTags(data: { tags: string[] }): Promise<Tag[]> {
        return RequestService.put('/nodes/:node/deployment-tags', data)
            .then(Parser.parse)
            .then((tags) => {
                useModelsStore().refreshModel('node');

                return tags;
            });
    }

    update(data: UpdateNodeRequest): Promise<Node> {
        return RequestService.put('/nodes/:node?include=tags,deploymentTags', data)
            .then(Parser.parse)
            .then((node) => {
                useModelsStore().refreshModel('node');

                return node;
            });
    }

    delete(): Promise<void> {
        return RequestService.delete('/nodes/:node');
    }

    daemonInfo(data: GetNodeDaemonInfoRequest): Promise<NodeDaemonInfo> {
        return RequestService.get(`/nodes/${data.id}/daemon-info`);
    }

    usageHistory(data: GetNodeDaemonInfoRequest): Promise<NodeUsageHistory> {
        return RequestService.get(`/nodes/${data.id}/usage-history`);
    }

    massDaemonInfo(data: GetMassNodeDaemonInfoRequest): Promise<Record<string, NodeDaemonInfo>> {
        return RequestService.get('/nodes/daemon-info', data);
    }

    // What billing has attached to this node's licence. Empty for a node with
    // no licence, and for one billing has not told us about -- the panel treats
    // both as "not entitled" everywhere else too.
    addons(data: GetNodeDaemonInfoRequest): Promise<NodeAddon[]> {
        return RequestService.get(`/nodes/${data.id}/addons`).then((response) => Parser.parse(response, true));
    }
}

export default new NodeService();
